import { createCipheriv, createDecipheriv, createHmac, hkdfSync, randomBytes } from "node:crypto";
import { envValue, type EnvRecord, type EnvVarSpec } from "./envPreflight";
import type { ContactKind } from "./contactDetect";

/**
 * B3 custody (Kabir 2026-10-11, ruling 2; CLAUDE.md section 10, A6.1). How a captured phone number or email is
 * held at rest.
 *
 * `contacts.phone` and `contacts.email` sit in plain text on the tier the agents read, so they stay unwritten. A
 * captured value goes to `contact_identities` (migration 0062) as AES-256-GCM ciphertext under a per-client subkey:
 * HKDF-SHA256 of the master key (env `ACKRA_IDENTITY_KEY`, the base64 of exactly 32 random bytes, read by NAME only)
 * and the client id. Each row has its own random 12-byte IV. The associated data binds the client, the contact and
 * the kind, so a row copied onto another contact, another client or the other kind fails authentication instead of
 * opening as somebody else's number.
 *
 * Dedupe without decrypting: `value_hash` is HMAC-SHA256 of the kind and the normalized value under a second HKDF
 * subkey of the same master and client, so one person's same number twice is one row, and two clients' equal
 * numbers hash apart.
 *
 * Erasure is row deletion (core/erasureStrategy.ts: rows deleted, no key destroyed). There is one key version (1);
 * rotation and crypto-shredding (F-26, counsel open) are not built.
 *
 * Every refusal names the env var and the rule broken, never a key or a value (section 10). The keyring keeps its
 * master key in a private field and prints as a placeholder.
 */

export const IDENTITY_KEY_ENV = "ACKRA_IDENTITY_KEY";
export const CONTACT_CAPTURE_FLAG_ENV = "ACKRA_ENABLE_CONTACT_CAPTURE";
/** The one key version this build seals under and opens. A row of another version is refused, never guessed at. */
export const IDENTITY_KEY_VERSION = 1;

const KEY_BYTES = 32;
const IV_BYTES = 12;
const TAG_BYTES = 16;
const HKDF_SALT = Buffer.from("ackra.identity.v1", "utf8");
const AAD_TAG = "ackra.identity.v1";

export class IdentityCustodyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "IdentityCustodyError";
  }
}

function refuse(message: string): never {
  throw new IdentityCustodyError(message);
}

/** The base64 of exactly 32 bytes. Refused loudly, never defaulted, never echoed. */
export function decodeIdentityKey(raw: string | undefined): Buffer {
  if (raw === undefined || raw.trim() === "") {
    refuse(`${IDENTITY_KEY_ENV} is not set; there is no default key, and no contact identity is sealed or opened without one`);
  }
  const text = raw.trim();
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(text) || text.length % 4 !== 0) refuse(`${IDENTITY_KEY_ENV} is not base64`);
  const bytes = Buffer.from(text, "base64");
  if (bytes.toString("base64") !== text) refuse(`${IDENTITY_KEY_ENV} is not canonical base64`);
  if (bytes.length !== KEY_BYTES) {
    refuse(`${IDENTITY_KEY_ENV} decodes to ${bytes.length} bytes; it must be the base64 of exactly ${KEY_BYTES} random bytes`);
  }
  return bytes;
}

/** The master key, held privately. Subkeys are derived per client on use and never cached. */
export class IdentityKeyring {
  readonly #master: Buffer;
  readonly keyVersion: number = IDENTITY_KEY_VERSION;

  constructor(master: Buffer) {
    if (master.length !== KEY_BYTES) refuse(`an identity keyring needs exactly ${KEY_BYTES} key bytes`);
    this.#master = Buffer.from(master);
  }

  /** @internal */
  subkey(purpose: "enc" | "mac", clientId: string): Buffer {
    if (clientId.trim() === "") refuse("an identity subkey needs a client id");
    return Buffer.from(hkdfSync("sha256", this.#master, HKDF_SALT, Buffer.from(`${purpose}:${clientId}`, "utf8"), KEY_BYTES));
  }

  toJSON(): string {
    return "[IdentityKeyring]";
  }

  toString(): string {
    return "[IdentityKeyring]";
  }

  [Symbol.for("nodejs.util.inspect.custom")](): string {
    return "[IdentityKeyring]";
  }
}

/** The keyring from `ACKRA_IDENTITY_KEY`. Throws when it is unset or malformed. */
export function readIdentityKeyring(env: EnvRecord): IdentityKeyring {
  return new IdentityKeyring(decodeIdentityKey(envValue(env, IDENTITY_KEY_ENV)));
}

/**
 * `ACKRA_ENABLE_CONTACT_CAPTURE`: exactly "1" is on; unset, empty or "0" is off (the default). Any other value is
 * refused rather than read as on or off.
 */
export function contactCaptureRequested(env: EnvRecord): boolean {
  const v = envValue(env, CONTACT_CAPTURE_FLAG_ENV);
  if (v === undefined || v === "0") return false;
  if (v === "1") return true;
  return refuse(`${CONTACT_CAPTURE_FLAG_ENV} must be exactly "1" (on) or "0" (off); any other value is refused rather than guessed at`);
}

/** Env preflight validator (envSpec): the flag's value as set. Null is good. */
export function validateEnableContactCapture(raw: string): string | null {
  if (raw === "0" || raw === "1") return null;
  return 'must be exactly "1" (contact capture on) or "0" (off). Any other value is refused rather than read as on';
}

/** Env preflight validator (envSpec): the key's value as set. Null is good. The complaint never carries the value. */
export function validateIdentityKey(raw: string): string | null {
  try {
    decodeIdentityKey(raw);
    return null;
  } catch (e) {
    return e instanceof IdentityCustodyError ? e.message : "is not a usable key";
  }
}

/**
 * B3. The two variables as the api and worker preflights declare them, one definition for both. The key is a
 * secret (never rendered) and is required only when the flag is "1": on with no key, or a malformed one, refuses
 * boot, so capture never runs keyless.
 */
export const CONTACT_CAPTURE_ENV_SPECS: readonly EnvVarSpec[] = [
  {
    name: CONTACT_CAPTURE_FLAG_ENV,
    purpose:
      "whether a phone or email a customer types is captured: sealed into contact_identities (consent not_given) and recorded as contact.captured {kind, source}",
    whenMissing: "allow",
    missingMeans: "off: nothing is detected, sealed or written",
    validate: validateEnableContactCapture,
    note: (env) =>
      envValue(env, CONTACT_CAPTURE_FLAG_ENV) === "1"
        ? `${CONTACT_CAPTURE_FLAG_ENV}=1: phones and emails customers type are sealed under ${IDENTITY_KEY_ENV} with consent not_given. ` +
          "Captured is not permission to text or email."
        : null,
  },
  {
    name: IDENTITY_KEY_ENV,
    secret: true,
    purpose: "the master key contact identities are sealed under (base64 of exactly 32 random bytes; a per-client subkey is derived from it)",
    whenMissing: "allow",
    missingMeans: "no contact identity can be sealed or opened; the owner's contact page shows counts only",
    validate: validateIdentityKey,
    requiredWhen: (env) =>
      envValue(env, CONTACT_CAPTURE_FLAG_ENV) === "1" ? `${CONTACT_CAPTURE_FLAG_ENV}=1 seals every captured detail under it` : null,
  },
];

/**
 * What ingest needs to capture: null when the flag is off (nothing is detected, sealed or written), the keyring
 * when it is on. On with an unset or malformed key throws: capture never runs keyless and never stores plaintext.
 */
export function resolveContactCapture(env: EnvRecord): IdentityKeyring | null {
  if (!contactCaptureRequested(env)) return null;
  return readIdentityKeyring(env);
}

export interface IdentityAddress {
  readonly clientId: string;
  readonly contactId: string;
  readonly kind: ContactKind;
}

export interface SealedIdentity {
  readonly ciphertext: Buffer;
  readonly iv: Buffer;
  readonly authTag: Buffer;
  readonly keyVersion: number;
  /** HMAC-SHA256 hex of the kind and the normalized value, per client. Dedupes; reveals nothing without the key. */
  readonly valueHash: string;
}

function aad(a: IdentityAddress): Buffer {
  return Buffer.from(JSON.stringify([AAD_TAG, a.clientId, a.contactId, a.kind]), "utf8");
}

export function identityValueHash(
  ring: IdentityKeyring,
  input: { readonly clientId: string; readonly kind: ContactKind; readonly value: string },
): string {
  return createHmac("sha256", ring.subkey("mac", input.clientId)).update(`${input.kind}:${input.value}`, "utf8").digest("hex");
}

export function sealIdentity(ring: IdentityKeyring, input: IdentityAddress & { readonly value: string }): SealedIdentity {
  if (input.value.length === 0) refuse("an empty contact identity is not sealed");
  if (input.contactId.trim() === "") refuse("a contact identity is sealed to a contact id");
  const iv = randomBytes(IV_BYTES);
  const cipher = createCipheriv("aes-256-gcm", ring.subkey("enc", input.clientId), iv, { authTagLength: TAG_BYTES });
  cipher.setAAD(aad(input));
  const ciphertext = Buffer.concat([cipher.update(input.value, "utf8"), cipher.final()]);
  return {
    ciphertext,
    iv,
    authTag: cipher.getAuthTag(),
    keyVersion: ring.keyVersion,
    valueHash: identityValueHash(ring, input),
  };
}

/**
 * Opens one row. Throws `IdentityCustodyError` on a key version this keyring does not hold, a malformed IV or tag,
 * or any authentication failure (tampered bytes, a row moved between contacts, clients or kinds, another key). The
 * error says which kind of failure, never the value.
 */
export function openIdentity(
  ring: IdentityKeyring,
  input: IdentityAddress & { readonly ciphertext: Buffer; readonly iv: Buffer; readonly authTag: Buffer; readonly keyVersion: number },
): string {
  if (input.keyVersion !== ring.keyVersion) {
    refuse(`a contact identity sealed under key version ${input.keyVersion} cannot be opened: this build holds key version ${ring.keyVersion} only`);
  }
  if (input.iv.length !== IV_BYTES || input.authTag.length !== TAG_BYTES) {
    refuse("a stored contact identity has a malformed IV or authentication tag and is refused");
  }
  try {
    const decipher = createDecipheriv("aes-256-gcm", ring.subkey("enc", input.clientId), input.iv, { authTagLength: TAG_BYTES });
    decipher.setAAD(aad(input));
    decipher.setAuthTag(input.authTag);
    return Buffer.concat([decipher.update(input.ciphertext), decipher.final()]).toString("utf8");
  } catch {
    return refuse(
      "a stored contact identity failed authentication: it was altered, moved to another contact, client or kind, " +
        `or sealed under a different ${IDENTITY_KEY_ENV}. It is refused, not shown.`,
    );
  }
}
